import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { CheckboxInput, Typography } from "@/components/atoms";
import { useGetVehicleFormOptionsQuery } from "@/redux/apis/vehicle/vehicleApi";
import { FilterDrawer } from "../common/FilterDrawer";
import { FilterPopover } from "../common/FilterPopover";
import { useVehicleFilters } from "../useVehicleFilters";

const schema = z.object({
  vehicleCategory: z.array(z.string()),
});

type FormData = z.infer<typeof schema>;

const useCategoryDisplayValue = (selected: (string | null)[] | null | undefined) => {
  const { data: options } = useGetVehicleFormOptionsQuery();

  return useMemo(() => {
    if (!selected) return undefined;
    const names = selected
      .filter(Boolean)
      .map((uuid) => options?.vehicleCategories.find((c) => c.uuid === uuid)?.name)
      .filter(Boolean) as string[];
    return names.length > 0 ? names.join(", ") : undefined;
  }, [selected, options]);
};

export const TypeFilter = () => {
  const {
    filters: { vehicleCategory },
    setFilter,
  } = useVehicleFilters();

  const value = useCategoryDisplayValue(vehicleCategory);

  const onClose = () => setFilter("vehicleCategory", undefined);

  return (
    <FilterPopover name="vehicleCategory" value={value} label="Vehicle Category" onClose={onClose}>
      <TypeFilterForm />
    </FilterPopover>
  );
};

interface TypeFilterDrawerProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const TypeFilterDrawer = ({ open, onOpenChange }: TypeFilterDrawerProps) => {
  const {
    filters: { vehicleCategory },
    setFilter,
  } = useVehicleFilters();

  const value = useCategoryDisplayValue(vehicleCategory);

  const onClose = () => setFilter("vehicleCategory", undefined);

  return (
    <FilterDrawer open={open} onOpenChange={onOpenChange} value={value} label="Vehicle Category" onClose={onClose}>
      <TypeFilterForm />
    </FilterDrawer>
  );
};

const TypeFilterForm = () => {
  const { setFilter, filters } = useVehicleFilters();
  const { isFetching, data: options } = useGetVehicleFormOptionsQuery();

  const categories = useMemo(() => {
    if (isFetching || !options) return [];

    return options.vehicleCategories
      .filter((category) => category.isActive)
      .map((category) => ({
        name: category.name,
        value: category.uuid,
      }));
  }, [isFetching, options]);

  const { control, setValue } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { vehicleCategory: (filters.vehicleCategory as string[]) ?? [] },
  });

  const handleCheckAll = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target;

      if (checked) {
        setFilter(
          "vehicleCategory",
          categories.map((c) => c.value)
        );
        setValue(
          "vehicleCategory",
          categories.map((c) => c.value)
        );
      } else {
        setFilter("vehicleCategory", null);
        setValue("vehicleCategory", []);
      }
    },
    [categories, setFilter, setValue]
  );

  const isAllSelected = useMemo(() => {
    if (!filters.vehicleCategory) return false;
    return filters.vehicleCategory.length === categories.length;
  }, [categories.length, filters.vehicleCategory]);

  return (
    <div className="contents">
      <Typography className="mb-4">Vehicle Category</Typography>
      <CheckboxInput label="All Vehicle Categories" onChange={handleCheckAll} variant="check" checked={isAllSelected} />
      <div className="mt-4 grid gap-4 ">
        <Controller
          control={control}
          name="vehicleCategory"
          render={({ field }) => (
            <>
              {categories.map((category) => (
                <CheckboxInput
                  key={category.value}
                  label={category.name}
                  value={category.value}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const next = [...field.value, e.target.value];
                      field.onChange(next);
                      setFilter("vehicleCategory", next.length > 0 ? next : null);
                    } else {
                      const next = field.value.filter((i) => i !== e.target.value);
                      field.onChange(next);
                      setFilter("vehicleCategory", next.length > 0 ? next : null);
                    }
                  }}
                  variant="check"
                  checked={field.value.includes(category.value)}
                />
              ))}
            </>
          )}
        />
      </div>
    </div>
  );
};
